/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";

import { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import { ApiResources } from "@/api/resources";
import VehicleQuickDownloadScheduleDialog from "@/components/DetailsSection/components/DetailsContent/components/ScheduleQuickDownload/VehicleQuickDownloadScheduleDialog/VehicleQuickDownloadScheduleDialog";
import DeleteTableItemsDialogContent from "@/components/Table/actions/dialogs/DeleteTableItemsDialogContent";
import DialogWithConfirmation from "@/components/Table/actions/dialogs/DialogWithConfirmation";
import RemoveSchedulesConfirmDialog from "@/components/Table/actions/dialogs/RemoveSchedulesConfirmDialog";
import RemoveSchedulesDialogContent from "@/components/Table/actions/dialogs/RemoveSchedulesDialogContent";
import useActionsForSelection from "@/components/Table/hooks/useActionsForSelection";
import { removeSlashFromPath } from "@/shared/functions/functions";
import { useTableStoreByRoute } from "@/stores/useTableStore";

import {
  ANALYSIS_PROVIDERS_ACTIONS,
  COMPANIES_ACTIONS,
  DRIVERS_ACTIONS,
  INTEGRATIONS_ACTIONS,
  INTEGRATIONS_API_ACTIONS,
  maxDialogWidth,
  ROUTES,
  ScheduleOption,
  TABLE_NAMES,
  tableNameParams,
  useMediaQueries,
  USERS_ACTIONS,
  VEHICLES_ACTIONS
} from "../../../../shared";
import DialogActions from "../../../../shared/components/buttons/DialogActions";
import { Actions } from "../../../PageHeader/actions/types/types";
import ActivateVehicleDialogContent from "../dialogs/Vehicles/ActivateVehicle/ActivateVehicleDialogContent";
import MoveVehiclesDialogContent from "../dialogs/Vehicles/MoveVehicles/MoveVehiclesDialogContent";
import {
  getActionForSelectionConfirmationButtonText,
  getActionForSelectionDialogDescription,
  getActionForSelectionDialogTitle,
  getPayloadObject
} from "../functions";
import {
  deleteActionsList,
  quickSchedulesList,
  removeSchedulesList
} from "./config";

interface IActionsForSelectionDialogProps {
  route: ROUTES;
  isOpen: boolean;
  resourceAction: Actions;
  close: (action: Actions) => void;
  onDefaultView: () => void;
}

const ActionsForSelectionDialog = ({
  route,
  isOpen,
  resourceAction,
  close,
  onDefaultView
}: IActionsForSelectionDialogProps) => {
  const {
    deleteSingleItem,
    deleteBulkOfItems,
    removeBulkOfItems,
    moveBulkItems,
    bulkApplyQuickSchedules,
    bulkActivateVehicles
  } = useActionsForSelection(route);

  const { selectedRows } = useTableStoreByRoute(route);

  const { t } = useTranslation();
  const { toMd, fromMd } = useMediaQueries();

  const currentTableName = removeSlashFromPath(route);

  const isDeleteResourceInResourcesActions = deleteActionsList.includes(
    resourceAction as any
  );

  const isRemoveSchedules = removeSchedulesList.includes(resourceAction as any);
  const isQuickScheduleDialog = quickSchedulesList.includes(
    resourceAction as any
  );

  const isMoveVehicles = resourceAction === VEHICLES_ACTIONS.MoveVehicles;

  const defaultReadyToSubmit =
    !isMoveVehicles && isDeleteResourceInResourcesActions;

  const [readyToSubmit, setReadyToSubmit] = useState(defaultReadyToSubmit);

  const [isLoading, setIsLoading] = useState(false);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);

  const [payload, setPayload] = useState<any>(null);
  const [schedules, setSchedules] = useState<ScheduleOption[]>([]);

  useEffect(() => {
    const isReady = isMoveVehicles ? Boolean(payload?.company) : !!payload;

    setReadyToSubmit(isReady);
  }, [payload]);

  const isDriversBulkSchedule =
    resourceAction === DRIVERS_ACTIONS.BulkQuickScheduleDownload;

  useEffect(() => {
    if (isDriversBulkSchedule) {
      setPayload(
        getPayloadObject(currentTableName as TABLE_NAMES, selectedRows)
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleCloseDialog = () => {
    isRemoveSchedules && setSchedules([]);
    setReadyToSubmit(false);
    close(resourceAction);
  };

  const isSingle = selectedRows.length === 1;
  const resourceCount = selectedRows?.length;
  const resourceParams = tableNameParams[currentTableName] || {};
  const companiesPage = route === ROUTES.Companies;
  const analysisProvidersPage = route === ROUTES.AnalysisProviders;

  const deleteSelectedItems = async () => {
    if (isSingle && !companiesPage && !analysisProvidersPage) {
      await deleteSingleItem(route, setIsLoading);
      handleCloseDialog();
    } else {
      await deleteBulkOfItems(
        route,
        payload,
        setIsLoading,
        companiesPage || analysisProvidersPage ? selectedRows.length : undefined
      );

      handleCloseDialog();
    }
    toMd && onDefaultView();
  };

  const removeSelectedSchedules = async (removeAction: Actions) => {
    setIsConfirmationOpen(false);
    await removeBulkOfItems(
      resourceParams?.apiResource,
      payload,
      removeAction,
      setIsLoading,
      handleCloseDialog
    );
  };

  const handleMoveVehicles = async () => {
    await moveBulkItems(
      resourceParams?.resourceName,
      payload,
      VEHICLES_ACTIONS.MoveVehicles,
      setIsLoading,
      handleCloseDialog
    );
  };

  const handleDriverBulkSchedule = async () => {
    await bulkApplyQuickSchedules(
      ApiResources.DriverSchedulesQuickDownloadBulk,
      payload,
      DRIVERS_ACTIONS.BulkQuickScheduleDownload,
      setIsLoading,
      handleCloseDialog
    );
  };

  const handleActivateVehicles = async (activate: boolean) => {
    const resourceAction = activate
      ? VEHICLES_ACTIONS.Activate
      : VEHICLES_ACTIONS.Deactivate;

    const fullPayload = {
      ...payload,
      activate
    };

    await bulkActivateVehicles(
      fullPayload,
      resourceAction,
      setIsLoading,
      handleCloseDialog
    );
  };

  const submitAction = async () => {
    switch (resourceAction) {
      case VEHICLES_ACTIONS.DeleteVehicle:
      case DRIVERS_ACTIONS.DeleteDriver:
      case COMPANIES_ACTIONS.DeleteCompany:
      case USERS_ACTIONS.DeleteUser:
      case INTEGRATIONS_ACTIONS.DeleteIntegration:
      case INTEGRATIONS_API_ACTIONS.DeleteCredential:
      case ANALYSIS_PROVIDERS_ACTIONS.Delete:
        return deleteSelectedItems();

      case VEHICLES_ACTIONS.RemoveSchedules:
      case DRIVERS_ACTIONS.RemoveSchedules:
        return setIsConfirmationOpen(true);

      case VEHICLES_ACTIONS.MoveVehicles:
        return handleMoveVehicles();

      case VEHICLES_ACTIONS.Activate:
        return handleActivateVehicles(true);

      case VEHICLES_ACTIONS.Deactivate:
        return handleActivateVehicles(false);

      case DRIVERS_ACTIONS.BulkQuickScheduleDownload:
        return handleDriverBulkSchedule();

      default:
        console.log(resourceAction);
        break;
    }
  };

  const renderDialogContent = () => {
    switch (resourceAction) {
      case VEHICLES_ACTIONS.DeleteVehicle:
      case DRIVERS_ACTIONS.DeleteDriver:
      case USERS_ACTIONS.DeleteUser:
      case COMPANIES_ACTIONS.DeleteCompany:
      case INTEGRATIONS_ACTIONS.DeleteIntegration:
      case INTEGRATIONS_API_ACTIONS.DeleteCredential:
      case ANALYSIS_PROVIDERS_ACTIONS.Delete:
        return (
          <DeleteTableItemsDialogContent
            selectedRows={selectedRows}
            setPayload={setPayload}
            resourceName={currentTableName as TABLE_NAMES}
          />
        );

      case VEHICLES_ACTIONS.MoveVehicles:
        return (
          <MoveVehiclesDialogContent
            selectedRows={selectedRows}
            setPayload={setPayload}
            resourceName={currentTableName as TABLE_NAMES}
          />
        );

      case VEHICLES_ACTIONS.RemoveSchedules:
      case DRIVERS_ACTIONS.RemoveSchedules:
        return (
          <RemoveSchedulesDialogContent
            selectedRows={selectedRows}
            resourceParams={resourceParams}
            schedules={schedules}
            setPayload={setPayload}
            setSchedules={setSchedules}
            isLoading={isLoading}
          />
        );

      case VEHICLES_ACTIONS.Activate:
      case VEHICLES_ACTIONS.Deactivate:
        return (
          <ActivateVehicleDialogContent
            selectedRows={selectedRows}
            setPayload={setPayload}
          />
        );

      default:
        return <></>;
    }
  };

  return (
    <>
      {!isQuickScheduleDialog && (
        <DialogWithConfirmation
          data-testid="actions-for-selection-dialog"
          title={getActionForSelectionDialogTitle(resourceAction, t)}
          isActionsSeparator={!isDriversBulkSchedule}
          isTitleSeparator
          open={isOpen}
          count={resourceCount}
          confirmationDescription={getActionForSelectionDialogDescription(
            resourceAction,
            t
          )}
          submit={submitAction}
          close={handleCloseDialog}
          css={css(fromMd && maxDialogWidth)}
          extendToMaxWidth={toMd}
          customChildrenStyle={
            isDriversBulkSchedule ? { padding: "0" } : undefined
          }
          actions={
            <div>
              <DialogActions
                onClose={handleCloseDialog}
                isLoading={isLoading}
                isreadyToConfirm={readyToSubmit}
                confirmationButtonText={getActionForSelectionConfirmationButtonText(
                  resourceAction,
                  t
                )}
                cancelButtonTestId={`cancel-${resourceAction}`}
                confirmationButtonTestId={`accept-${resourceAction}`}
                cancelButtonText={t("Button##cancel")}
              />
            </div>
          }
        >
          {renderDialogContent()}
        </DialogWithConfirmation>
      )}

      {isRemoveSchedules && (
        <RemoveSchedulesConfirmDialog
          schedules={schedules}
          resourceParams={resourceParams}
          isOpen={isConfirmationOpen}
          toMd={toMd}
          isDialogLoading={isLoading}
          setDialogOpen={setIsConfirmationOpen}
          removeSelectedSchedules={removeSelectedSchedules}
        />
      )}

      {isQuickScheduleDialog && (
        <VehicleQuickDownloadScheduleDialog
          vehicleId={selectedRows}
          isOpen={isOpen}
          handleClose={handleCloseDialog}
          isMultipleIds
        />
      )}
    </>
  );
};

export default ActionsForSelectionDialog;
